import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { fetchMarketingSummary, type MarketingSummary } from '../../lib/marketing-summary';
import { formatDate } from '../../lib/format';

/**
 * Chip naast de KortingPill: heeft deze klant ooit een kortingsmail gehad?
 *
 * Aanleiding 13-08-2026: een klant claimde 10% als vaste klant, de medewerker
 * kon het niet nakijken en gaf de korting. Het bewijs stond in
 * `marketing_sends` maar niet op het scherm waar de vraag viel. Deze chip zet
 * het antwoord naast de knop die de korting toepast.
 */

interface MarketingContextChipProps {
  accountId: string | null | undefined;
}

/**
 * Module-cache per account: het order-detail rendert deze chip op meerdere
 * plekken (pagina + drawer) en medewerkers openen dezelfde klant vaak
 * meermaals achter elkaar. Eén lookup per klant per 5 minuten is genoeg.
 */
const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map<string, { at: number; value: MarketingSummary }>();

async function loadSummary(accountId: string): Promise<MarketingSummary> {
  const hit = cache.get(accountId);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.value;
  const value = await fetchMarketingSummary(accountId);
  cache.set(accountId, { at: Date.now(), value });
  return value;
}

export function MarketingContextChip({ accountId }: MarketingContextChipProps) {
  const [summary, setSummary] = useState<MarketingSummary | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setSummary(null);
    setFailed(false);
    loadSummary(accountId)
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch(() => {
        // Non-critical: de chip verdwijnt, de rest van het scherm blijft heel.
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!accountId || failed) return null;

  if (!summary) {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium border bg-slate-50 text-slate-400 border-slate-200">
        Marketing…
      </span>
    );
  }

  const heeftAanbod = summary.verdict === 'kortingsaanbod-verstuurd';
  const topOffer = summary.discountOffers[0] ?? null;

  const label = (() => {
    if (heeftAanbod && topOffer) {
      return `Kortingsmail ${topOffer.percentage}% · ${formatDate(topOffer.sentAt)}`;
    }
    if (summary.verdict === 'marketingmail-zonder-kortingsaanbod') {
      return `Geen kortingsmail (${summary.sentCount} mails)`;
    }
    return 'Geen marketingmail';
  })();

  const chipClass = clsx(
    'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium border cursor-pointer hover:ring-1 hover:ring-slate-300',
    heeftAanbod
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-slate-50 text-slate-500 border-slate-200'
  );

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={chipClass}
        title={summary.summary}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {label}
      </button>

      {open ? (
        <div
          role="dialog"
          className="absolute right-0 top-full mt-1 z-20 w-72 bg-white border border-slate-200 rounded-lg shadow-lg p-3"
        >
          <p className="text-xs text-slate-700 leading-relaxed">{summary.summary}</p>

          {summary.discountOffers.length > 0 ? (
            <ul className="mt-2 space-y-1.5 border-t border-slate-100 pt-2">
              {summary.discountOffers.slice(0, 5).map((offer) => (
                <li key={offer.sendId} className="text-[11px] text-slate-600">
                  <span className="font-medium text-slate-800">{offer.label}</span>
                  <br />
                  {formatDate(offer.sentAt)}
                  {offer.code ? ` · code ${offer.code}` : ' · zonder code'}
                  {offer.scope === 'ondertapijt' ? ' · alleen ondertapijt' : ''}
                </li>
              ))}
            </ul>
          ) : null}

          <p className="mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-400">
            {summary.sentCount} marketingmail(s) verstuurd
            {summary.contacts.length > 1
              ? ` · ${summary.contacts.length} gekoppelde klantprofielen`
              : ''}
            .
            {topOffer?.code
              ? ' Let op: deze code is niet persoonlijk, dus hij kan gedeeld zijn.'
              : ''}
          </p>
        </div>
      ) : null}
    </div>
  );
}
